import {
  Badge,
  EmptyTableState,
  Loading,
} from '@startupquickstart/frontend';
import { CheckCircle2, type LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import type {
  HealthCheckResponse,
  HealthRow,
  HealthRowKind,
} from '@/hooks/useHealthCheck';

interface HealthCheckListProps {
  data: HealthCheckResponse['data'] | undefined;
  isLoading?: boolean;
  /** Icon shown in the all-clear state. */
  IconAllClear: LucideIcon;
  /** Title shown in the all-clear state. */
  allClearTitle: string;
  /** Sub-text shown in the all-clear state. */
  allClearDescription: string;
  /** Title shown in the empty-scope state (no rows at all). */
  emptyTitle: string;
  emptyDescription: string;
  /** Optional bulk action area rendered above the list (e.g. "Generate all"). */
  toolbar?: React.ReactNode;
  /** Optional per-row action area. Receives the row; returns nodes. */
  renderRowActions?: (row: HealthRow) => React.ReactNode;
}

const ROW_LINK: Record<HealthRowKind, (row: HealthRow) => string | null> = {
  attraction: (row) => `/attractions/${row.id}/overview`,
  theme: (row) =>
    row.parentId ? `/attractions/${row.parentId}/themes` : null,
  'theme-translation': (row) =>
    row.parentId ? `/attractions/${row.parentId}/themes` : null,
  'attraction-translation': (row) =>
    row.parentId ? `/attractions/${row.parentId}/translations` : null,
  tour: (row) => `/tours/${row.id}/overview`,
  'tour-translation': (row) =>
    row.parentId ? `/tours/${row.parentId}/translations` : null,
};

/**
 * Human-readable label for what kind of entity each row represents. Lets
 * the user scan a mixed list (primary, theme, translation) and tell at a
 * glance what they're regenerating.
 */
const ROW_LEVEL_LABEL: Record<HealthRowKind, string> = {
  attraction: 'Primary',
  tour: 'Primary',
  theme: 'Theme',
  'attraction-translation': 'Primary translation',
  'tour-translation': 'Primary translation',
  'theme-translation': 'Theme translation',
};

const ROW_LEVEL_CLASS: Record<HealthRowKind, string> = {
  attraction: 'text-blue-700 bg-blue-50 border-blue-200',
  tour: 'text-blue-700 bg-blue-50 border-blue-200',
  theme: 'text-violet-700 bg-violet-50 border-violet-200',
  'attraction-translation': 'text-teal-700 bg-teal-50 border-teal-200',
  'tour-translation': 'text-teal-700 bg-teal-50 border-teal-200',
  'theme-translation': 'text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200',
};

function issueVariantClass(type: HealthRow['issues'][number]['type']): string {
  switch (type) {
    case 'missing':
      return 'text-amber-700 bg-amber-50 border-amber-200';
    case 'out-of-sync':
      return 'text-slate-700 bg-slate-50 border-slate-200';
    case 'missing-translation':
      return 'text-rose-700 bg-rose-50 border-rose-200';
  }
}

/**
 * Renders any HealthCheckResponse — one row per entity with issues, each
 * row showing the entity link, all issue chips, and (optionally) per-row
 * actions supplied by the caller. The shell handles loading, empty, and
 * all-clear states so the per-check page wrappers stay one-liners.
 */
export function HealthCheckList({
  data,
  isLoading,
  IconAllClear,
  allClearTitle,
  allClearDescription,
  emptyTitle,
  emptyDescription,
  toolbar,
  renderRowActions,
}: HealthCheckListProps) {
  const rows = useMemo(() => data?.rows ?? [], [data]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <EmptyTableState
        icon={<IconAllClear className="h-6 w-6" />}
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
        <CheckCircle2 className="h-5 w-5" />
        <div>
          <p className="text-sm font-semibold">{allClearTitle}</p>
          <p className="text-xs">{allClearDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {toolbar}
      <div className="text-xs text-muted-foreground">
        {rows.length} of {data.total} need attention
      </div>
      <div className="space-y-2">
        {rows.map((row) => (
          <HealthRowCard
            key={`${row.kind}:${row.id}`}
            row={row}
            actions={renderRowActions?.(row)}
          />
        ))}
      </div>
    </div>
  );
}

function HealthRowCard({
  row,
  actions,
}: {
  row: HealthRow;
  actions?: React.ReactNode;
}) {
  const href = ROW_LINK[row.kind](row);

  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border bg-card p-4 shadow-sm">
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={`gap-1 ${ROW_LEVEL_CLASS[row.kind]}`}>
            {ROW_LEVEL_LABEL[row.kind]}
          </Badge>
          {href ? (
            <Link
              to={href}
              className="truncate text-sm font-semibold text-primary hover:underline"
            >
              {row.label}
            </Link>
          ) : (
            <span className="truncate text-sm font-semibold">{row.label}</span>
          )}
          {row.issues.map((issue, idx) => (
            <Badge
              key={`${issue.type}-${issue.field}-${idx}`}
              variant="outline"
              className={`gap-1 ${issueVariantClass(issue.type)}`}
            >
              {issue.label}
            </Badge>
          ))}
        </div>
      </div>
      {actions ? <div className="flex shrink-0 gap-2">{actions}</div> : null}
    </div>
  );
}
